import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  connectRealtime,
  disconnectRealtime,
  isRealtimeConnected,
  subscribeRealtimeStatus,
  watchPresence,
  watchedPresence,
  type PresenceUpdate,
} from '@/lib/realtime';
import { MessageApiService } from '@/lib/services/message-api';
import {
  clearTyping,
  loadAlertPrefs,
  notePresence,
  noteTyping,
  playChime,
} from '@/components/messaging/live-status';
import { isMuted, previewText } from '@/components/messaging/chat-utils';

/** Query keys of every inbox (personal, college, company) — refreshed on inbox signals. */
const INBOX_KEYS = new Set(['conversations', 'college-conversations', 'company-conversations']);

interface Signal {
  conversationId: string;
  reason?: string;
}

/**
 * Keeps one realtime connection open while signed in and turns server signals
 * into React Query refetches. Mount once, in the app shell.
 */
export function useMessagingRealtime(enabled: boolean, currentUserId?: string): void {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!enabled) return;
    const socket = connectRealtime();

    /** A new message elsewhere: chime and/or desktop notification, per this browser's settings. */
    const alertNewMessage = async (conversationId: string) => {
      const prefs = loadAlertPrefs();
      if (!prefs.desktop && !prefs.sound) return;
      const openHere =
        !document.hidden &&
        new URLSearchParams(window.location.search).get('conversationId') === conversationId;
      if (openHere) return;
      const conv = await MessageApiService.getConversation(conversationId).catch(() => null);
      if (!conv || isMuted(conv) || conv.lastMessageSenderId === currentUserId) return;
      if (prefs.sound) playChime();
      if (
        prefs.desktop &&
        document.hidden &&
        'Notification' in window &&
        Notification.permission === 'granted'
      ) {
        const name =
          conv.type === 'APPLICATION' && conv.companyName
            ? conv.companyName
            : (conv.participants[0]?.name ?? 'New message');
        const note = new Notification(name, { body: previewText(conv), tag: conversationId });
        note.onclick = () => {
          window.focus();
          const inbox = conv.inboxOrganizationId ? '/college/messages' : '/personal/messages';
          navigateRef.current(`${inbox}?conversationId=${conversationId}`);
          note.close();
        };
      }
    };

    const refreshInboxes = () =>
      queryClient.invalidateQueries({
        predicate: (q) => INBOX_KEYS.has(String(q.queryKey[0])),
      });
    const onMessages = ({ conversationId }: Signal) =>
      queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
    const onConversations = ({ conversationId, reason }: Signal) => {
      void refreshInboxes();
      if (reason === 'new') {
        clearTyping(conversationId);
        void alertNewMessage(conversationId);
      }
      // A new message may have created or updated a notification.
      if (reason === 'new' || reason === 'read') {
        void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      }
    };
    const onTyping = ({ conversationId, userId }: { conversationId: string; userId: string }) =>
      noteTyping(conversationId, userId);
    const onPresence = (update: PresenceUpdate) => notePresence([update]);

    // After a gap (sleep, network drop) catch up on anything missed.
    const onConnect = () => {
      void refreshInboxes();
      void queryClient.invalidateQueries({ queryKey: ['messages'] });
      const ids = watchedPresence();
      if (ids.length) void watchPresence(ids).then(notePresence);
    };

    socket.on('messages:changed', onMessages);
    socket.on('conversations:changed', onConversations);
    socket.on('typing', onTyping);
    socket.on('presence', onPresence);
    socket.on('connect', onConnect);
    return () => {
      socket.off('messages:changed', onMessages);
      socket.off('conversations:changed', onConversations);
      socket.off('typing', onTyping);
      socket.off('presence', onPresence);
      socket.off('connect', onConnect);
      disconnectRealtime();
    };
  }, [enabled, queryClient, currentUserId]);
}

/** True while live updates are flowing — pages poll much less often then. */
export function useRealtimeConnected(): boolean {
  return useSyncExternalStore(subscribeRealtimeStatus, isRealtimeConnected, () => false);
}
